import { PAGE_NAMES } from '@Components/Page/page-constants';
import { MMM_RUN_BRANCH } from './dtc-commerce-mmm-constants';
import { FixedMmmRun } from './utils/dtc-commerce-mmm-runs-discovery';

/**
 * The surfaces this dashboard serves. They render the same tree and differ only in
 * naming — and, from the model_type PR onward, in which runs they ask the metrics lambda
 * for. Anything that needs to differ between them belongs in the config below rather than
 * in a branch inside a component.
 */
export const MmmDashboardVariant = {
  DTC: 'dtc',
  COMMERCE: 'commerce',
  GEO: 'geo'
} as const;
export type MmmDashboardVariantType =
  (typeof MmmDashboardVariant)[keyof typeof MmmDashboardVariant];

export interface MmmDashboardConfig {
  // Which runs this surface asks the metrics lambda for. A database value from the
  // FK-constrained hroas_mmm_model_type lookup (dtc | commerce | geo | brand_equity) —
  // deliberately not the DataQA `ModelType` const, which is a UI vocabulary that already
  // diverges on brand_equity (it spells that one `brandEquity`).
  //
  // A run only carries a model_type if its model config sets one; a config still on the
  // legacy `rmmm` vocabulary is written as NULL upstream and won't appear on either page.
  modelType: string;
  // Page header, and the noun in the empty state ("No <title> data").
  title: string;
  // Analytics label — drives document.title and the GA4/Mixpanel page type.
  pageType: string;
  // Leading segment of the filename for anything downloaded from this page.
  exportPrefix: string;
  // Show this one run instead of discovering runs. For a surface whose runs aren't registered
  // anywhere discovery can list them yet.
  fixedRun?: FixedMmmRun;
  // Show the market views, for a surface whose runs are modelled by DMA.
  showMarkets?: boolean;
}

export const DTC_DASHBOARD_CONFIG: MmmDashboardConfig = {
  modelType: MmmDashboardVariant.DTC,
  title: PAGE_NAMES.DTC_MMM,
  pageType: PAGE_NAMES.DTC_MMM,
  exportPrefix: 'dtc-mmm'
};

export const COMMERCE_DASHBOARD_CONFIG: MmmDashboardConfig = {
  modelType: MmmDashboardVariant.COMMERCE,
  title: PAGE_NAMES.COMMERCE_MMM,
  pageType: PAGE_NAMES.COMMERCE_MMM,
  exportPrefix: 'commerce-mmm'
};

// Pinned to DSW's in-store sales run: the Meridian pipeline doesn't register geo runs yet, so
// there is nothing for discovery to list. Remove fixedRun and give geo real run discovery once
// geo runs are registered (Geo-MMM Front End Refresh, DEV-25036).
export const GEO_DASHBOARD_CONFIG: MmmDashboardConfig = {
  modelType: MmmDashboardVariant.GEO,
  title: PAGE_NAMES.GEO_MMM,
  // Kept apart from the legacy /geo-mmm page's analytics until this page replaces it.
  pageType: PAGE_NAMES.GEO_MMM_NEW,
  exportPrefix: 'geo-mmm',
  fixedRun: {
    client: 'dsw',
    branch: MMM_RUN_BRANCH,
    kpi: 'In_Store_Sales',
    timestamp: '2026-10-19T21:53:56',
    // This run's own file layout, not the discovery floor, so it can't drift if the floor moves.
    outputFileVersion: '2'
  },
  showMarkets: true
};

// A pinned run was never registered, so there is no run row for a client-facing toggle to update.
export const allowsClientFacingToggle = (config: MmmDashboardConfig): boolean => !config.fixedRun;
